import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '../../routes/routingContract';
import './TrialUpgradeCard.css';

function clampPercent(used, limit) {
  if (!limit) return 0;
  return Math.min(100, Math.max(0, Math.round((used / limit) * 100)));
}

export default function TrialUpgradeCard({
  billingStatus,
  compact = false,
  onUpgradeClick,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  if (billingStatus?.billing_status !== 'trialing') return null;

  const messagesUsed = billingStatus.messages_used ?? 0;
  const messagesLimit = billingStatus.messages_limit ?? 10;
  const messagesRemaining = billingStatus.messages_remaining ?? Math.max(0, messagesLimit - messagesUsed);
  const plaidUsed = billingStatus.plaid_items_connected ?? 0;
  const plaidLimit = billingStatus.plaid_items_limit ?? 1;
  const messageLimitReached = Boolean(billingStatus.message_limit_reached);
  const plaidLimitReached = Boolean(billingStatus.plaid_limit_reached);
  const title = messageLimitReached
    ? 'Free trial limit reached'
    : 'Free trial usage';
  const summary = compact
    ? messageLimitReached
      ? '0 messages left'
      : `${messagesRemaining} message${messagesRemaining === 1 ? '' : 's'} left`
    : messageLimitReached
    ? 'Upgrade to keep chatting.'
    : 'Upgrade anytime for continued chat access and more connected accounts.';

  const handleUpgrade = () => {
    if (onUpgradeClick) {
      onUpgradeClick();
      return;
    }

    navigate(APP_ROUTES.upgrade, {
      state: {
        backgroundPath: location.pathname,
        upgradeMessage: messageLimitReached
          ? 'You hit your free message limit. Upgrade to keep chatting.'
          : plaidLimitReached
          ? 'You used your free connected account. Upgrade to add more accounts.'
          : 'You can upgrade now or continue with your free trial.',
      },
    });
  };

  return (
    <section className={`trial-upgrade-card${compact ? ' compact' : ''}`} aria-label="Free trial usage">
      <div className="trial-upgrade-card-header">
        <div>
          <div className="trial-upgrade-kicker">{title}</div>
          <p>{summary}</p>
        </div>
        <button type="button" onClick={handleUpgrade}>
          Upgrade
        </button>
      </div>

      <div className="trial-usage-rows">
        <div className="trial-usage-row">
          <div className="trial-usage-label">
            <span>Messages</span>
            <strong>{messagesUsed}/{messagesLimit}</strong>
          </div>
          <div className="trial-usage-track" aria-hidden="true">
            <span style={{ width: `${clampPercent(messagesUsed, messagesLimit)}%` }} />
          </div>
        </div>

        <div className="trial-usage-row">
          <div className="trial-usage-label">
            <span>Connected accounts</span>
            <strong>{plaidUsed}/{plaidLimit}</strong>
          </div>
          <div className="trial-usage-track" aria-hidden="true">
            <span style={{ width: `${clampPercent(plaidUsed, plaidLimit)}%` }} />
          </div>
        </div>
      </div>
    </section>
  );
}
